<template>
  <div id="app">
    <DriveAdminConsole v-if="$route.name !== 'MapModal'" />
    <HeaderBar v-if="!$store.state.isMobile" />
    <MobileHeaderBar v-else />
    <div class="content">
      <router-view />
    </div>
    <a
      href="https://www.getty.edu/"
      id="getty"
      v-if="!($store.state.isMobile && $route.name === 'about')"
      ><img :src="gettyLogoPath"
    /></a>

    <DriveTour :enabled="$store.state.tours.drive" />
    <SortTour :enabled="$store.state.tours.sort" />
  </div>
</template>

<script>
import DriveAdminConsole from "./components/DriveAdminConsole";
import DriveTour from "./components/DriveTour";
import HeaderBar from "./components/HeaderBar";
import MobileHeaderBar from "./components/MobileHeaderBar";
import SortTour from "./components/SortTour";
import { getAssetPath } from "@/constants/constants";

export default {
  name: "App",

  components: {
    DriveAdminConsole,
    DriveTour,
    HeaderBar,
    MobileHeaderBar,
    SortTour
  },

  mounted() {
    this.$store.dispatch("loadImageData");
    this.$store.dispatch("loadSunsetGeojson");
  },

  computed: {
    gettyLogoPath() {
      return getAssetPath("/img/getty@2x.png");
    }
  }
};
</script>

<style lang="scss">
@import "@/constants/_variables.scss";

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-Medium-Web.woff2");
  font-weight: 500;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-Regular-Web.woff2");
  font-weight: 400;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-MediumItalic-Web.woff2");
  font-weight: 500;
  font-style: italic;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-RegularItalic-Web.woff2");
  font-weight: 400;
  font-style: italic;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-Light-Web.woff2");
  font-weight: 300;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-LightItalic-Web.woff2");
  font-weight: 300;
  font-style: italic;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-Bold-Web.woff2");
  font-weight: 700;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-BoldItalic-Web.woff2");
  font-weight: 700;
  font-style: italic;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-Semibold-Web.woff2");
  font-weight: 600;
}

@font-face {
  font-family: "Graphik";
  src: url($font-base-path+"/Graphik-SemiboldItalic-Web.woff2");
  font-weight: 600;
  font-style: italic;
}

* {
  box-sizing: border-box;
}

html {
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: "Graphik", "Open Sans", sans-serif;
  height: 100%;
}

body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: black;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  overflow: hidden;
}

#nav {
  height: 30px;
  display: flex;
  justify-content: flex-start;
  border-bottom: 1px solid #ccc;
  width: 100%;

  a,
  div {
    margin: 0.25em 1em;
  }

  a {
    color: black;
  }

  div {
    color: gray;
  }

  a:hover {
    border-bottom: 2px solid black;
  }
}

.content {
  height: calc(100% - 67px);
  height: -webkit-fill-available;
  width: 100%;

  @media (orientation: landscape) and (max-width: 815px) {
    height: 100%;
  }
}

.card {
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);

  &.interactive:hover {
    cursor: pointer;
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  }
}

.button {
  background-color: $copper;
  color: $white;
  padding: 0.5em 1em;
  display: inline;
  font-style: italic;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: $copper;
    background-color: $light-gold;
  }
}

.nbutton {
  padding: 0em 7px;
  height: 30px;
  cursor: pointer;
  background-color: $copper;
  color: $white;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  font-weight: normal;

  &:not(.disabled):hover,
  &.highlighted {
    background-color: $light-gold;
    color: $copper;

    path {
      fill: $copper;
    }
  }

  path {
    fill: white;
  }

  div {
    margin-top: -2px;
  }

  img {
    padding-left: 0.5em;
  }

  svg {
    padding-left: 0.5em;
    height: 19px;
    width: 19px;
    margin-top: -1px;
  }
}

.chip {
  cursor: pointer;

  &.dragged {
    opacity: 0.5;
  }

  .chip-interior {
    height: 33px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px;
    background-color: $black;
    color: $white;
    font-size: 13px;

    &:hover {
      background-color: $light-gold;
      color: black;

      .close {
        filter: invert(0%);
      }
    }

    .close {
      width: 16px;
      height: 16px;
      margin-left: 1em;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      filter: invert(100%);

      img {
        width: 100%;
      }
    }
  }

  margin-left: 0.5em;
  margin-right: 0.5em;

  &:last-child {
    margin-right: 0em;
  }
}

.mgl-map-wrapper {
  z-index: -1;
}

#getty {
  img {
    width: 63px;
    position: fixed;
    left: 0px;
    bottom: 0px;
  }

  @media (max-width: 815px) {
    transform-origin: 0% 0%;
    // transform: scale(0.75) rotate(90deg) translateX(-63px);
    transform: scale(0.75);
  }

  @media (max-width: 815px) and (orientation: landscape) {
    transform: scale(0.75) rotate(90deg) translateX(-63px);
  }
}
</style>
